import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Leaf, Users, Package } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { fetchSummaryCounts } from "../../features/summary/summarySlice";

// 🔹 Helper: deep comparison
const isEqual = (a, b) => {
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

const StatsSection = () => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { data: summaryData, error } = useSelector((state) => state.summary);

  const [displayData, setDisplayData] = useState(null);
  const lastDataRef = useRef(null); // simpan data sebelumnya tanpa trigger render

  // 🔹 Fetch pertama + interval tiap 1 detik
  useEffect(() => {
    dispatch(fetchSummaryCounts());
    const interval = setInterval(() => {
      dispatch(fetchSummaryCounts());
    }, 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // 🔹 Update UI hanya jika data API berubah
  useEffect(() => {
    if (!summaryData) return;

    // ambil data sebelumnya dari ref
    const last = lastDataRef.current;

    // cek apakah data berubah
    if (!isEqual(summaryData, last)) {
      // hanya update jika data benar-benar beda
      setDisplayData(summaryData);
      lastDataRef.current = summaryData;
    }
  }, [summaryData]);

  const countNasabah = displayData?.count_nasabah || 0;
  const countPetugas = displayData?.count_cabang || 0;
  const countCabang = displayData?.total_sampah_terkumpul || 0;

  return (
    <section
      id="stats-section"
      style={{
        padding: "5rem 1.5rem",
        background: "white",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {error ? (
          <p className="text-red-500 text-center">Gagal memuat data summary</p>
        ) : !displayData ? (
          <p className="text-center text-gray-500">Memuat data...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Nasabah */}
            <div className="text-center">
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "64px",
                  height: "64px",
                  background: "rgba(148, 242, 127, 0.1)",
                  borderRadius: "50%",
                  marginBottom: "1rem",
                }}
              >
                <Users size={32} style={{ color: "rgb(13, 121, 22)" }} />
              </div>
              <div
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                  fontWeight: 700,
                  color: "rgb(0, 55, 32)",
                  marginBottom: "0.5rem",
                }}
              >
                {countNasabah.toLocaleString()}+
              </div>
              <div
                style={{
                  fontSize: "1rem",
                  color: "rgb(131, 146, 140)",
                  fontWeight: 500,
                }}
              >
                {t.stats.customersLabel}
              </div>
            </div>

            {/* Petugas */}
            <div className="text-center">
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "64px",
                  height: "64px",
                  background: "rgba(148, 242, 127, 0.1)",
                  borderRadius: "50%",
                  marginBottom: "1rem",
                }}
              >
                <Package size={32} style={{ color: "rgb(13, 121, 22)" }} />
              </div>
              <div
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                  fontWeight: 700,
                  color: "rgb(0, 55, 32)",
                  marginBottom: "0.5rem",
                }}
              >
                {countPetugas.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: "1rem",
                  color: "rgb(131, 146, 140)",
                  fontWeight: 500,
                }}
              >
                {t.stats.officersLabel}
              </div>
            </div>

            {/* Cabang */}
            <div className="text-center">
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "64px",
                  height: "64px",
                  background: "rgba(148, 242, 127, 0.1)",
                  borderRadius: "50%",
                  marginBottom: "1rem",
                }}
              >
                <Leaf size={32} style={{ color: "rgb(13, 121, 22)" }} />
              </div>
              <div
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                  fontWeight: 700,
                  color: "rgb(0, 55, 32)",
                  marginBottom: "0.5rem",
                }}
              >
                {countCabang.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: "1rem",
                  color: "rgb(131, 146, 140)",
                  fontWeight: 500,
                }}
              >
                {t.stats.recycledLabel}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default StatsSection;
